/**
 * 能力舱卡(轻量版,首页 5 群卡墙的**非 dept 舱**用)—— icon 舞台 + 三态标 + 一句话 + 来源标 + CTA。
 *
 * ★与内容 4 舱的 `WorkbenchDeptCard` 分工(记忆:统一规格不硬套同一组件):dept 舱有主角图 +
 *   creditRates 积分标 + hover 真样图,走那个组件;这里是服务型/占位舱(声量/客服/陪练 + 融入中/即将),
 *   无主角图、无落地积分标,只 icon + 状态。视觉语言(形状锁 CARD_WALL_RADIUS / .scene-* / 皮肤 token)复用,保持一墙齐整。
 *
 * ★三态诚实:已开(active,可点进 route)/ 融入中(integrating,琥珀,不可点)/ 即将(coming_soon,降饱和,不假点)。
 *   名称 / 简介取后端 catalog(displayName / description,i18n key 优先、回退后端中文);来源标 / chips / 形制走前端 i18n overlay。
 */
import type { KeyboardEvent } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { capFigureOf } from "@/entities/scene-card/model/figures";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import type {
  CabinPresentation,
  CapabilityCard as CapabilityCardData,
} from "@/entities/capability";

interface Props {
  card: CapabilityCardData;
  pres: CabinPresentation;
  /** 已开舱点击(路由由页面层做);融入中 / 即将不传或不触发。 */
  onOpen?: () => void;
}

const STATUS_PILL: Record<CapabilityCardData["status"], string> = {
  active: "text-success bg-success/15",
  integrating: "text-warning bg-warning/15",
  coming_soon: "text-muted-foreground bg-muted",
};

export function CapabilityCard({ card, pres, onOpen }: Props) {
  const { t } = useTranslation();
  const active = card.status === "active";
  const clickable = active && !!onOpen;
  const Icon = pres.icon;
  // 右侧人物(挂小人,2026-07-27):切皮肤自动换人;404 静默降级回 icon 舞台
  const skin = useActiveSkin();
  const fig = capFigureOf(skin, card.imageKey ?? card.capability);

  const statusKey = card.status === "coming_soon" ? "coming" : card.status;
  const ctaLabel = active ? t(`cabin.cta.${pres.ctaVerb ?? "enter"}`) : t(`cabin.cta.${statusKey}`);
  const title = t(`cabin.name.${card.capability}`, { defaultValue: card.displayName });
  const desc = t(`cabin.desc.${card.capability}`, { defaultValue: card.description ?? "" });
  const source = pres.sourceLabelKey ? t(pres.sourceLabelKey) : null;
  const kind = pres.kindKey ? t(pres.kindKey) : null;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.();
    }
  };

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-disabled={clickable ? undefined : true}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={clickable ? onKeyDown : undefined}
      data-capability-card={card.capability}
      data-status={card.status}
      className={cn(
        "scene-cap group relative flex flex-col overflow-hidden border bg-card text-left",
        CARD_WALL_RADIUS,
        clickable ? "cursor-pointer" : "cursor-default"
      )}
    >
      {/* 右侧人物(挂小人):素材底色=皮肤 --card 真值,向左渐隐;占位舱同步降饱和 */}
      <span
        className={cn("scene-litefig", !active && "opacity-70 saturate-[.55]")}
        style={{ backgroundImage: `url(${fig})` }}
        aria-hidden
      />
      {/* ── 舞台:图标(icon 让位到左侧非人物区);占位态降饱和 ─────────────────
       * ★icon-only 卡不套 scene-stage 的 aspect 1/0.82:那是给满幅主角图的场景卡用的,
       *   一张小图标配 aspect 会在 ≥300px 宽时撑出一个高空盒(旧巨型空卡)。这里给**固定矮高度**
       *   (h-28),保留 scene-stage 的皮肤内阴影,只把比例锁换成定高。
       * ★舞台切 bg-card 不再 bg-muted:人物素材四周烘的是 --card 真值,muted 上会露底色硬边。 */}
      <div
        className={cn(
          "scene-stage relative grid h-28 w-full place-items-center bg-card pr-[38%]",
          !active && "opacity-70 saturate-[.55]"
        )}
      >
        <Icon className="h-11 w-11 text-primary/90" strokeWidth={1.4} aria-hidden />
        {kind && (
          <span className="scene-shape absolute bottom-2 left-2 z-30 inline-flex items-center rounded-full bg-card/85 px-2 py-1 text-2xs font-medium leading-none text-card-foreground backdrop-blur-sm">
            {kind}
          </span>
        )}
      </div>

      {/* ── 文案区 ──────────────────────────────────────────────────────────── */}
      <div className="relative z-30 flex flex-1 flex-col gap-1 p-3">
        <div className="flex items-center gap-2 pr-[24%]">
          <h3
            className={cn(
              "text-sm font-[650] tracking-[-0.012em]",
              !active && "text-muted-foreground"
            )}
          >
            {title}
          </h3>
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-semibold",
              STATUS_PILL[card.status]
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {t(`cabin.status.${statusKey}`)}
          </span>
        </div>
        <p className="line-clamp-2 pr-[24%] text-2xs leading-[1.62] text-muted-foreground">
          {desc}
        </p>
        {pres.chipKeys && pres.chipKeys.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1 pr-[24%] pt-1.5">
            {pres.chipKeys.map((k) => (
              <span
                key={k}
                className="rounded-full border border-border/60 bg-background/50 px-1.5 py-0.5 text-2xs text-muted-foreground"
              >
                {t(k)}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── 底栏:来源标 + CTA(已开可点;占位灰字不假点)────────────────────── */}
      <div className="relative z-30 flex items-center justify-between border-t border-border/60 px-3 py-2">
        <span className="text-2xs text-muted-foreground">{source}</span>
        <span
          className={cn(
            "inline-flex items-center gap-0.5 text-2xs font-semibold",
            active ? "text-primary" : "font-medium text-muted-foreground"
          )}
        >
          {ctaLabel}
          {active && <ArrowRight className="scene-cta-arrow h-3 w-3" />}
        </span>
      </div>
    </div>
  );
}
